import { isNil } from "lodash";
import { LandingLayoutWithoutSidebar } from "PFApp/components/landing_layout";
import { useOnboarding } from "PFApp/onboarding/use_onboarding";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import AutoSelect from "PFComponents/select/autoselect";
import { fetchAutocompleteSkills } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import React, { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import OnboardingBox from "../parts/onboarding_box";
import OnboardingHeader from "../parts/onboarding_header";
import SkillsPageSuggestions from "../parts/skills_step_suggestions";
import css from "./common.less";

const ENOUGH_SKILLS = 3;

const SkillsStep = () => {
  const { ready, stepDotsProps, suggestedProfile, currentProfile, saveProfile, navigateNext, navigateBack } =
    useOnboarding("skills");
  const { t } = useTranslation("onboarding");

  const skillsToSave = (skills) =>
    skills.map((item) => {
      const clone = { ...item };

      if ((clone.text || clone.text === 0) && !clone.value && clone.value !== 0) {
        clone.value = clone.text;
      }
      if (!clone.weight && clone.weight !== 0) {
        clone.weight = null;
      }
      // clear <Autoselect>'s `item` prop and default skill fields not to override them
      ["item", "experience", "interest", "top"].forEach((prop) => delete clone[prop]);

      return clone;
    });

  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalCanceled, setModalCanceled] = useState(false);

  useEffect(() => {
    saveProfile({ skills: skillsToSave(skills) });
  }, [skills]);

  useEffect(() => {
    if (suggestedProfile) {
      setSkills(suggestedProfile.skills || []);
    }
  }, [!!suggestedProfile]);

  const next = () => {
    saveProfile({ registration_status: "active" }).then(navigateNext);
  };

  const back = () => {
    saveProfile({ registration_status: "new" }); // new is before details
    navigateBack();
  };

  const save = () => {
    setIsLoading(true);
    saveProfile({ skills: skillsToSave(skills) }).then((resp) => {
      // modalCanceled because we want to only show the modal once and not nag the user
      const enoughSkills = (resp.skills?.length || 0) >= ENOUGH_SKILLS;
      const anyTop = (resp.skills || []).filter(({ top }) => top).length > 0;
      if (enoughSkills || anyTop || modalCanceled) {
        next();
      } else {
        setIsLoading(false);
        setShowModal(true);
      }
    });
  };

  if (!suggestedProfile) {
    return false;
  }

  const anySuggestedSkill = (suggestedProfile.skills || []).length > 0;

  /** skills come from /api/autocomplete/custom_values and /api/me
   * with id's missing on autocomplete and global_id's missing on me
   * in this case there is a need fo filter  by skill text for some cases */
  const isSkillNotSelected = (option) =>
    !skills.find((skill) => {
      const globalSkillId = skill.global_id || skill.globalId;

      return (
        skill.id === option.id ||
        (!isNil(globalSkillId) && globalSkillId === option.globalId) ||
        skill.text === option.text
      );
    });

  return (
    <LandingLayoutWithoutSidebar alignTop>
      <SkillsPageSuggestions skills={skills || []} setSkills={setSkills} currentProfile={currentProfile} />
      <OnboardingBox
        big
        qaId="Onboarding.SkillsStep"
        stepDotsProps={stepDotsProps}
        isLoading={isLoading || !ready}
      >
        <OnboardingHeader title={t("skills")} back={back} next={save} />

        <p className={css.tip}>{anySuggestedSkill ? t("determinedSkillsFromCV") : t("tellUsYourSkills")}</p>

        <AutoSelect
          key={JSON.stringify(skills.map(({ id }) => id)) /* make the component controlled */}
          letClear
          label={t("listYourSkills")}
          placeholder={t("typeSkillHere")}
          multi
          values={skills}
          query={(term) => fetchAutocompleteSkills({ term })}
          filterOptions={(resp) => resp.filter(isSkillNotSelected)}
          formatOption={(item) => ({
            id: item.id || item.global_id || item.text,
            text: item.text,
            displayElement: item.text,
            item: item
          })}
          handleChange={(newSkills) =>
            setSkills(
              newSkills.map((skill) =>
                skill.id ? skill : { text: skill.text, value: skill.text, global_id: skill.globalId }
              )
            )
          }
        />
      </OnboardingBox>

      {showModal && !modalCanceled && (
        <ConfirmModal
          closeOnBackdropClick
          labelCancel={t("yesContinue")}
          labelOK={t("cancelAndAddMore")}
          handleClose={() => {
            setModalCanceled(true);
            next();
          }}
          handleOK={() => setModalCanceled(true)}
        >
          {t("areYouSure")}
        </ConfirmModal>
      )}
    </LandingLayoutWithoutSidebar>
  );
};

export default SkillsStep;
